/**
 * What `GET /api/conversations` and `GET /api/conversations/:id` return.
 *
 * Moved out of `app/stores/chats.ts` and out of an inline literal in `app/stores/web-chat.ts`. See
 * `shared/library/libraryPrompt.ts` for why a shape with timestamps is declared once and viewed
 * twice.
 *
 * ── THE SUMMARY NOW CARRIES THE CHAT'S SCOPE ─────────────────────────────────────────────────────
 *
 * It did not, for a while, and this comment's job was to say so. `listConversations` selected
 * `c.id, c.title, c.is_favorite, c.created_at, c.updated_at` and a `COUNT(*)` — not `c.case_id`, not
 * `c.client_id`, though both are columns of `conversations`. The type declared them anyway, as
 * optional, so nothing ever failed to compile:
 *
 *   - `createConversation` pushes an optimistic row carrying the scope it just set;
 *   - `setConversationScope` writes `conv.case_id` / `conv.client_id` back onto the cached row;
 *   - the next `listConversations()` replaced both with `undefined`, because the wire never
 *     carried them. NOT on a 30-second timer, which is the obvious misreading: `fetchIfStale`
 *     calls `listConversations()` in BOTH branches and `STALE_MS` only decides whether it is
 *     awaited. So the overwrite happened on every app-shell mount (`app/pages/app.vue`) and
 *     every visit to history — on navigation, not on a clock.
 *
 * Which is why "is this conversation part of this case" (`app/pages/app/cases/[id]/index.vue`) went
 * quietly false after any refresh. The projection now selects both columns — it already grouped by
 * `c.id`, so nothing else about that query changed — and both fields come off the wire.
 *
 * `is_favorite` was optional and is not: the column is `NOT NULL` and the projection always selects
 * it.
 */

interface ConversationSummaryFields<D> {
  id: string
  title: string
  is_favorite: boolean
  /** `COUNT(conversation_messages.id)` for this thread. */
  message_count: number
  created_at: D
  updated_at: D
  /**
   * The chat's STORED scope rung — exactly one is ever set (`conversations_single_scope_rung`), and
   * `null` genuinely means global.
   *
   * Optional only because of the OPTIMISTIC row `createConversation` pushes before the server
   * answers; every row that came off the wire carries both.
   *
   * **Stored, not active.** Since scope became frontend-owned this is what the chat may OFFER to
   * restore when nothing is selected — never the app's current context. That is
   * `useWebChatStore().activeScope`.
   */
  case_id?: string | null
  client_id?: string | null
  /**
   * The case this chat is FILED under — the one it was started from (MAR-1468). Unlike `case_id`, it
   * never follows the scope: it is what decides whether the chat belongs on a case's list. Optional
   * for the same optimistic-row reason as the two above.
   */
  filed_case_id?: string | null
}

/**
 * Does this conversation belong on that case's list? (MAR-1468.)
 *
 * Reads `filed_case_id` — the case the conversation was started from — and NEVER `case_id`, which is
 * the chat's scope and moves on every "Acotar" / "Quitar ámbito". Used by the case hub and the context
 * rail to decide whether a conversation that just appeared needs their list refetched.
 */
export function isFiledUnderCase(
  row: { filed_case_id?: string | null } | null | undefined,
  caseId: string | null | undefined,
): boolean {
  return !!caseId && !!row && row.filed_case_id === caseId
}

/** Returned by `listConversations`. */
export type ConversationSummaryRow = ConversationSummaryFields<Date>
/** Received by the client from `GET /api/conversations`. */
export type ConversationSummary = ConversationSummaryFields<string>

/**
 * THE WHOLE ROW — every column of `conversations`, and nothing else.
 *
 * `POST /api/conversations`, `PATCH /api/conversations/:id` (rename and scope) all used to answer
 * with `RETURNING *`, which is not a contract: adding a column to the table changed what those three
 * endpoints returned and nothing anywhere had to be edited. The service's projection is spelled out
 * against this type now, so a new column reaches the wire only when someone adds it here too.
 */
interface ConversationRowFields<D> {
  id: string
  title: string
  is_favorite: boolean
  /**
   * `conversations.created_by` is `text`, not a uuid — but it holds the INTERNAL `users.id`
   * (`requireOrganization` hands the service `user.id`), not the Firebase uid. This comment claimed
   * the opposite until MAR-428; the distinction matters because `created_by` is the ONLY owner this
   * table has — it carries no `organization_id` — so every read is scoped by it.
   */
  created_by: string | null
  /** Exactly one of these is ever set: `conversations_single_scope_rung` is a CHECK constraint. */
  case_id: string | null
  client_id: string | null
  /**
   * The case the chat is FILED under (MAR-1468): set from the creating scope's case and never moved
   * by a scope change. The case list and the case's conversation count read this, not `case_id`.
   */
  filed_case_id: string | null
  /**
   * The jurisdiction picked for this chat (MAR-1418); `null` = the org's main. What a turn RUNS on
   * is re-resolved server-side against the org's current list (`server/services/chat/jurisdiction.ts`).
   */
  jurisdiction: string | null
  created_at: D
  updated_at: D
  open_task_id: string | null
  open_task_context_id: string | null
}

/** Returned by `createConversation` / `updateConversationTitle` / `setConversationScope`. */
export type ConversationRow = ConversationRowFields<Date>
/** Received by the client from `POST /api/conversations` and `PATCH /api/conversations/:id`. */
export type Conversation = ConversationRowFields<string>

/**
 * What `GET /api/conversations/:id` returns: the whole row — unlike the summary, which is a
 * projection — plus four resolved names off the case/client joins. A case-scoped chat carries its
 * client too, so the scope chip can read "Client › Case" without a second fetch.
 *
 * `messages` stays `any[]`: the row is `conversation_messages.*` with `tool_calls` replaced by
 * normalised `ChatToolCall`s, and the client remaps every field by hand in `loadConversation`.
 * Declaring it properly means declaring the message row, which is its own contract and its own
 * change.
 */
interface ConversationDetailFields<D> extends ConversationRowFields<D> {
  /** From the `cases` join. */
  case_name: string | null
  /** The case's OWN client — not `client_id`, which is null whenever `case_id` is set. */
  case_client_id: string | null
  case_client_name: string | null
  /** From the `clients` join on `c.client_id`, for a client-scoped chat. */
  client_name: string | null
  messages: any[]
}

/** Returned by `getConversationWithMessages`. */
export type ConversationDetailRow = ConversationDetailFields<Date>
/** Received by the client from `GET /api/conversations/:id`. */
export type ConversationDetail = ConversationDetailFields<string>

/**
 * One persisted message as `POST`/`PATCH /api/conversations/:id/messages` return it
 * (`messageRow` in `server/services/conversations`). `timestamp` is cast to text in the projection,
 * so it is a string on both sides; `tool_calls` is the raw jsonb column.
 */
export interface ConversationMessageRecord {
  id: string
  role: string
  content: string
  timestamp: string
  tool_calls: unknown
}
